import { AlertCircle, Eye, EyeOff, type LucideIcon } from 'lucide-react'
import { useState, type ComponentProps, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type IconInputProps = ComponentProps<'input'> & { icon: LucideIcon; revealable?: boolean }

export function IconInput({ icon: Icon, revealable, type, className, ...props }: IconInputProps) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  return (
    <div className="group/input relative">
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within/input:text-primary" aria-hidden />
      <Input
        type={revealable && visible ? 'text' : type}
        className={cn('h-11 rounded-xl bg-muted/30 pl-10 dark:bg-muted/20', revealable && 'pr-11', className)}
        {...props}
      />
      {revealable ? (
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={t(visible ? 'auth.hidePassword' : 'auth.showPassword')}
          aria-pressed={visible}
          className="absolute right-1.5 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      ) : null}
    </div>
  )
}

export function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-xs text-destructive">{message}</p> : null
}

export function FormAlert({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="flex items-start gap-2.5 rounded-xl border border-destructive/20 bg-destructive/[0.06] px-3.5 py-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p className="min-w-0">{children}</p>
    </div>
  )
}

export function AuthHeading({ eyebrow, title, subtitle }: { eyebrow?: ReactNode; title: ReactNode; subtitle?: ReactNode }) {
  return (
    <div className="space-y-2">
      {eyebrow}
      <h1 className="text-[28px] font-semibold leading-tight tracking-tight">{title}</h1>
      {subtitle ? <p className="text-sm leading-relaxed text-muted-foreground">{subtitle}</p> : null}
    </div>
  )
}
